import { describeHistoryProgress, workedCounts } from '../demo/demoHistory.js'
import { useEffect, useState } from 'react'
import { onboardingStepsFor, type OnboardingProgress, type OnboardingStepId } from './onboardClient.js'
import type { DemoChoice } from '../demo/demoPlan.js'
import { isBlankPlan } from '../demo/demoRules.js'
import { OnboardingCredentials } from './OnboardingCredentials.js'
import { downloadPrivateKey } from '../../partner/index.js'
import { Link } from '../../core/index.js'
import { ArrowLeft, Check, Circle, Download, LoaderCircle, X } from 'lucide-react'
import { OpenAsMenu } from '../../accounts/index.js'
import { Button, ButtonGlyph, buttonClass } from '../../core/index.js'
import type { ComponentProps } from 'react'

/** The admin calls this screen makes itself, and what the screens it opens need. */
type AdminApi = ComponentProps<typeof OpenAsMenu>['api']


export interface OnboardingRunState {
  progress: OnboardingProgress
  running: OnboardingStepId | null
  failure: { step: OnboardingStepId; message: string } | null
  isFinished: boolean
  /** The demo choice when this run includes the demo-data steps, so the step list shows them from the start; null for a real client. */
  demo: DemoChoice | null
}

/** How far a long-running step has got: so many of so many for any step that makes many calls, the history as the API writes it. */
function runningDetail(stepId: OnboardingStepId, progress: OnboardingProgress): string | null {
  // Any step that makes many calls says how many of how many are done.
  if (progress.stepCount?.step === stepId) {
    const { done, total, noun } = progress.stepCount
    return `${done.toLocaleString()} of ${total.toLocaleString()} ${noun}`
  }
  if (stepId === 'demo-history' && progress.demo?.history) {
    const { counts } = progress.demo.history
    const worked = workedCounts(counts)
    const shifts = counts.expectedShifts ? `${counts.shifts.toLocaleString()} of ${counts.expectedShifts.toLocaleString()} shifts published` : `${counts.shifts.toLocaleString()} shifts published`
    return `${describeHistoryProgress(progress.demo.history)} · ${shifts} · ${counts.runs} months built · ${counts.outcomes.toLocaleString()} worked shifts reported · ${(worked.covered + worked.pickups).toLocaleString()} gaps covered`
  }
  return null
}

function StepList({ run }: { run: OnboardingRunState }) {
  return (
    <ol className="space-y-1.5 mb-6">
      {onboardingStepsFor(run.demo).map((step) => {
        const isDone = run.progress.completed.includes(step.id)
        const isFailed = run.failure?.step === step.id
        const isRunning = run.running === step.id && !isFailed
        const Marker = isDone ? Check : isFailed ? X : isRunning ? LoaderCircle : Circle
        const tone = isDone ? 'text-green' : isFailed ? 'text-coral' : isRunning ? 'text-marine' : 'text-ink-mid'
        const detail = isRunning ? runningDetail(step.id, run.progress) : null
        return (
          <li key={step.id} className={`text-sm flex gap-2 ${tone}`}>
            <Marker aria-hidden="true" strokeWidth={2.5} className={`mt-0.5 h-4 w-4 shrink-0 ${isRunning ? 'animate-spin' : ''} ${isDone || isFailed || isRunning ? '' : 'opacity-40'}`} />
            <span className={isDone || isRunning || isFailed ? 'font-bold' : ''}>
              {step.label}
              {detail && <span className="font-normal text-ink-mid"> · {detail}</span>}
            </span>
          </li>
        )
      })}
    </ol>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">{label}</div>
      <div className="text-xs text-ink font-mono break-all">{value}</div>
    </div>
  )
}

/**
 * Progress while onboarding runs, then what it produced. The private key and
 * the operator's password exist only in this page - neither is stored by the
 * server (it keeps a public key and a password hash) or by the browser - so
 * this screen is the only chance to hand them over, including after a
 * failure partway through.
 */
export function OnboardingOutcome({ run, onStartAnother, api }: { run: OnboardingRunState; onStartAnother: () => void; api?: AdminApi }) {
  const { progress, failure } = run
  const [hasDownloadedKey, setHasDownloadedKey] = useState(false)
  const hasUnsavedKey = Boolean(progress.key) && !hasDownloadedKey

  useEffect(() => {
    if (!hasUnsavedKey) {
      return
    }
    function warn(event: BeforeUnloadEvent): void {
      event.preventDefault()
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [hasUnsavedKey])

  return (
    <div className="bg-white border border-marine/15 p-6">
      <StepList run={run} />

      {api && run.isFinished && !failure && run.demo && progress.client && (
        <div className="border border-gold/60 bg-gold/10 p-4 mb-6">
          <div className="text-xs font-extrabold text-ink mb-1">Open the demo as one of its people</div>
          <p className="text-[11px] text-ink-mid mb-2">A new tab, signed in as the person you pick, for an hour and without a password. The credentials below still work for a real sign-in.</p>
          <OpenAsMenu api={api} clientId={progress.client.clientId} />
        </div>
      )}

      {failure && (
        <div className="border border-coral bg-coral-lt p-4 mb-6 text-sm text-ink">
          <div className="font-bold text-coral mb-1">Onboarding stopped partway</div>
          <p className="mb-2">{failure.message}</p>
          {progress.registrationId && (
            <p className="text-xs">
              The registration already exists, so don't submit the form again for
              this client. {run.demo && progress.client
                ? 'As a demo client it can be deleted from its panel under All Clients, then set up again.'
                : 'Finish the remaining steps with the onboarding CLI or the API, or delete the registration if it has no client ID yet.'}
            </p>
          )}
        </div>
      )}

      {run.isFinished && !failure && (
        <div className="mb-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm font-bold text-green">{run.demo ? (isBlankPlan({ size: run.demo.size }) ? 'Blank demo client ready.' : 'Demo client ready.') : 'Client onboarded.'}</div>
            {/* The way back, where the eye lands when the run finishes - not only at the foot of the page. The new client is in the list when it opens. */}
            <Link to="/admin" className={buttonClass({ size: 'sm' })}>
              <ButtonGlyph icon={ArrowLeft} size="sm" />
              Back to All Clients
            </Link>
          </div>
          {run.demo && isBlankPlan({ size: run.demo.size }) && (
            <p className="text-xs text-ink-mid mt-1">
              It has no facilities or workers yet. Facilities are the operator's to register: sign in to the operator dashboard with the login below and use{' '}
              <span className="font-bold">New facility</span>. Then add workers, operators and facility administrators here with the New buttons on their pages.
            </p>
          )}
        </div>
      )}

      {(progress.registrationId || progress.client) && (
        <div className="grid gap-4 sm:grid-cols-2 mb-6">
          {progress.client && <Field label="Client ID" value={progress.client.clientId} />}
          {progress.key && <Field label="Key ID (kid)" value={progress.key.kid} />}
          {progress.client && <Field label="Allowed scopes" value={progress.client.allowedScopes.join(' ')} />}
          {progress.client && <Field label="Token endpoint" value={progress.client.tokenEndpoint} />}
        </div>
      )}

      {progress.key && (
        <div className="border border-marine/15 p-4 mb-4">
          <div className="text-xs font-bold text-ink mb-1">Partner private key</div>
          <p className="text-xs text-ink-mid mb-3">
            Generated in this browser and never sent to SeeWhoCares's servers. Download it now and pass it to the partner over a secure
            channel, then delete your copy - it cannot be recovered after you leave this page.
          </p>
          <Button
            icon={Download}
            onClick={() => {
              downloadPrivateKey(progress.key!)
              setHasDownloadedKey(true)
            }}
          >
            {hasDownloadedKey ? 'Download again' : 'Download private key'}
          </Button>
        </div>
      )}

      {progress.demo && (
        <div className="text-xs text-ink mb-6">
          <span className="font-bold">Demo data:</span> {progress.demo.facilities ?? 0} {progress.demo.facilities === 1 ? 'facility' : 'facilities'}
          {progress.demo.addresses !== undefined && <> ({progress.demo.addresses} with an address)</>} · {progress.demo.workers ?? 0} workers ·{' '}
          {progress.demo.shifts ?? 0} shifts published
          {progress.demo.history && (
            <>
              {' '}
              · {progress.demo.history.counts.runs} months built · {workedCounts(progress.demo.history.counts).covered} call-outs covered, {workedCounts(progress.demo.history.counts).pickups} open seats picked up, {workedCounts(progress.demo.history.counts).ranShort} run short, {workedCounts(progress.demo.history.counts).swaps} swaps
            </>
          )}
          {progress.demo.outcomes !== undefined && <> · {progress.demo.outcomes} shift outcomes</>}
          {progress.demo.behaviorEvents !== undefined && <> · {progress.demo.behaviorEvents} custom behavior events</>}
          {progress.demo.rankings !== undefined && <> · {progress.demo.rankings} rankings</>}
          {progress.demo.closedPeriods !== undefined && <> · {progress.demo.closedPeriods} {progress.demo.closedPeriods === 1 ? 'period' : 'periods'} closed</>}
          {progress.demo.addOnSwitches !== undefined && <> · {progress.demo.addOnSwitches} add-on {progress.demo.addOnSwitches === 1 ? 'switch' : 'switches'} on</>}
          {progress.demo.summaries !== undefined && <> · {progress.demo.summaries} performance {progress.demo.summaries === 1 ? 'summary' : 'summaries'}</>}
          {progress.demo.bridgeCallout && (
            <>
              {' '}
              · a Clinical Bridge callout open at {progress.demo.bridgeCallout.facilityName}, {progress.demo.bridgeCallout.poolSize === 1 ? `${progress.demo.bridgeCallout.workerName} told` : `${progress.demo.bridgeCallout.poolSize} told`} - open the worker app as {progress.demo.bridgeCallout.workerName} to answer it
            </>
          )}
          {progress.demo.insights && <> · Executive Insights added up for {progress.demo.insights.facilities} {progress.demo.insights.facilities === 1 ? 'facility' : 'facilities'} - the Insights page in the operator app has the portfolio</>}
          {progress.demo.scheduleRun && (
            <>
              {' '}
              · {progress.demo.scheduleRun.period} built and released at {progress.demo.scheduleRun.facilityName} from {progress.demo.scheduleRun.workersWithPreferences} workers' preferences: {progress.demo.scheduleRun.scheduled} scheduled, {progress.demo.scheduleRun.assigned} assigned, {progress.demo.scheduleRun.contests} contested {progress.demo.scheduleRun.contests === 1 ? 'shift' : 'shifts'}, {progress.demo.scheduleRun.unfilled} unfilled - the Tools tab has the run and each worker's Shifts tab their month
            </>
          )}
        </div>
      )}

      {progress.credentials.length > 0 && <OnboardingCredentials credentials={progress.credentials} />}

      {(run.isFinished || failure) && (
        <div className="flex items-center gap-4">
          <Link to="/admin" className="inline-flex items-center gap-1 text-xs font-bold text-marine hover:text-marine-dk">
            <ArrowLeft aria-hidden="true" className="h-3.5 w-3.5" />
            All clients
          </Link>
          <button type="button" onClick={onStartAnother} disabled={hasUnsavedKey} className="text-xs font-bold text-marine disabled:opacity-40">
            Onboard another client
          </button>
          {hasUnsavedKey && <span className="text-[11px] text-ink-mid">Download the private key first.</span>}
        </div>
      )}
    </div>
  )
}
